import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  updateFirstPageData,
  getHealthEnquiryAPI,
} from '../../Redux/HealthRedux';

// Image imports for self, spouse, parent, inLaw, children (male/female)
import maleChild from '../../assets/health-member/child_male.png';
import femaleChild from '../../assets/health-member/child_female.png';
import selfMale from '../../assets/health-member/self_male.png';
import selfFemale from '../../assets/health-member/self_female.png';
import spouseMale from '../../assets/health-member/spose_male.png';
import spouseFemale from '../../assets/health-member/spose_female.png';
import parentMale from '../../assets/health-member/parent_male.png';
import parentFemale from '../../assets/health-member/parent_female.png';
import inLawsMale from '../../assets/health-member/inLaw_male.png';
import inLawsFemale from '../../assets/health-member/inLaw_female.png';

import aadharLogo from '../../assets/aadhaar.svg';
import { calculateAge } from '../../utilities/calculateAge';

// Styled Components
const FormContainer = styled.div`
  width: 80%;
  margin: 40px auto;
  padding: 30px;
  background: linear-gradient(to bottom right, #fff, #f9f9ff);
  border-radius: 20px;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.08);
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 800;
  text-align: center;
  margin-bottom: 24px;
  color: #222;
`;

const NoticeBox = styled.div`
  background-color: #fff5e0;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 15px;
  font-weight: 500;
  color: #333;
`;

const MemberCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px 25px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  font-weight: 700;
  color: #444;
  margin-bottom: 16px;
`;

const Icon = styled.span`
  font-size: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #444;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  font-size: 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  outline: none;
  margin-bottom: 0; /* Remove margin so spacing is handled by FormRow */
  background-color: #fefefe;

  &:focus {
    border-color: #f6931e;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 14px;
  font-size: 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  outline: none;
  margin-bottom: 0;
  background-color: #fefefe;

  &:focus {
    border-color: #f6931e;
  }
`;

  const FormRow = styled.div`
    display: flex;
    justify-content: flex-start;
    gap: 20px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  `;

const InputWrapper = styled.div`
  flex: 1 1 30%; /* grow and shrink with basis 30% of container */
  min-width: 220px; /* minimum width for small screens */
  max-width: 33%; /* max width to keep 3 inputs per row nicely */
`;


const NextButton = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(to right, #f6931e, #ff6a3a);
  color: white;
  font-size: 16px;
  font-weight: bold;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  margin-top: 20px;
  box-shadow: 0px 6px 14px rgba(255, 105, 0, 0.3);
  transition: all 0.3s ease-in-out;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0px 8px 18px rgba(255, 105, 0, 0.4);
  }
`;

const Span = styled.span`
  color: red;
`;

const CoverageForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [enquiryId, setEnquiryId] = useState(null);
  let combinedRedux = useSelector((state) => state.health.combinedRedux);

const [formData, setFormData] = useState({
  self: {
    firstName: null,
    lastName: null,
    dob: null,
    relation: null,
  },
  spouse: {
    firstName: null,
    lastName: null,
    dob: null,
    relation: null,
  },
  parent: {
    firstName: null,
    lastName: null,
    dob: null,
    relation: null,
  },
  inLaw: {
    firstName: null,
    lastName: null,
    dob: null,
    relation: null,
  },
  child: [],
});
  const [validationErrors, setValidationErrors] = useState({});

  // Get enquiryId from URL params
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const id = queryParams.get('enquiryId');
    setEnquiryId(id);
  }, []);

  // Get the data from Redux
  const septateCombinedRedux = useSelector(
    (state) => state.health.septateCombinedRedux
  );

  // Set the default form data based on septateCombinedRedux
  useEffect(() => {
    const updatedFormData = { ...formData };

    if (septateCombinedRedux?.selectedMembers?.self === true) {
      updatedFormData.self = {
        firstName: septateCombinedRedux?.insuredMembers?.self?.firstName,
        lastName: septateCombinedRedux?.insuredMembers?.self?.lastName,
        dob: septateCombinedRedux?.insuredMembers?.self?.dob,
        relation: "self",
        gender: septateCombinedRedux?.insuredMembers?.self?.gender,
        pan: septateCombinedRedux?.insuredMembers?.self?.pan,
        pincode: septateCombinedRedux?.insuredMembers?.self?.pincode,
        email: septateCombinedRedux?.insuredMembers?.self?.email,
      };
    }

    if (septateCombinedRedux?.selectedMembers?.spouse === true) {
      updatedFormData.spouse = {
        firstName: septateCombinedRedux?.insuredMembers?.spouse?.firstName,
        lastName: septateCombinedRedux?.insuredMembers?.spouse?.lastName,
        dob: septateCombinedRedux?.insuredMembers?.spouse?.dob,
        relation: "spouse",
        gender: septateCombinedRedux?.insuredMembers?.spouse?.gender,
      };
    }

    if (septateCombinedRedux?.selectedMembers?.parent === true) {
      updatedFormData.parent = {
        firstName: septateCombinedRedux?.insuredMembers?.parent?.firstName,
        lastName: septateCombinedRedux?.insuredMembers?.parent?.lastName,
        dob: septateCombinedRedux?.insuredMembers?.parent?.dob,
        relation: "parent",
        gender: septateCombinedRedux?.insuredMembers?.parent?.gender,
      };
    }

    if (septateCombinedRedux?.selectedMembers?.inLaw === true) {
      updatedFormData.inLaw = {
        firstName: septateCombinedRedux?.insuredMembers?.inLaw?.firstName,
        lastName: septateCombinedRedux?.insuredMembers?.inLaw?.lastName,
        dob: septateCombinedRedux?.insuredMembers?.inLaw?.dob,
        relation: "inLaw",
        gender: septateCombinedRedux?.insuredMembers?.inLaw?.gender,
      };
    }

    // Adding children to the form data (male/female combined in a single array)
if (septateCombinedRedux?.insuredMembers?.child) {
  updatedFormData.child = septateCombinedRedux.insuredMembers.child.map(
    (child) => {
      let relation = null;

      if (child.gender === 'male') {
        relation = 'Son';
      } else if (child.gender === 'female') {
        relation = 'Daughter';
      }

      return {
        ...child,
        relation,
      };
    }
  );
}


    setFormData(updatedFormData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [septateCombinedRedux]);

  useEffect(() => {
    if (enquiryId) {
      dispatch(getHealthEnquiryAPI({ enquiryId }));
    }
  }, [enquiryId, dispatch]);

  const validateCoverageForm = (formData, septateCombinedRedux) => {
    const errors = {};

    const validateMemberAge = (member, type, index) => {
      if (member?.dob) {
        const age = calculateAge(member.dob);

        // Proposer age validation (18 to 85 years)
        if (type === 'self' && (age < 18 || age > 85)) {
          errors[`${type}_dob`] =
            'Proposer or Self must be between 18 and 85 years old';
        }

        // Child age validation (1 day to 25 years)
        if (type === 'child' && (age < 1 || age > 25)) {
          errors[`child_${index}_dob`] =
            'Child must be between 1 day and 25 years old';
        }
      }
    };

    const validateMember = (member, type) => {
      if (member) {
        if (!member.firstName?.trim()) {
          errors[`${type}_firstName`] = 'First name is required';
        }
        if (!member.lastName?.trim()) {
          errors[`${type}_lastName`] = 'Last name is required';
        }
        if (!member.dob) {
          errors[`${type}_dob`] = 'Date of birth is required';
        }
      }
      validateMemberAge(member, type);
    };

    const validateChild = (children) => {
      if (children && children.length > 0) {
        children.forEach((child, index) => {
          if (!child.firstName?.trim()) {
            errors[`child_${index}_firstName`] = 'First name is required';
          }
          if (!child.lastName?.trim()) {
            errors[`child_${index}_lastName`] = 'Last name is required';
          }
          if (!child.dob) {
            errors[`child_${index}_dob`] = 'Date of birth is required';
          }
          validateMemberAge(child, 'child', index);
        });
      }
    };

    if (septateCombinedRedux?.selectedMembers?.self)
      validateMember(formData.self, 'self');
    if (septateCombinedRedux?.selectedMembers?.spouse)
      validateMember(formData.spouse, 'spouse');
    if (septateCombinedRedux?.selectedMembers?.parent)
      validateMember(formData.parent, 'parent');
    if (septateCombinedRedux?.selectedMembers?.inLaw)
      validateMember(formData.inLaw, 'inLaw');
    validateChild(formData.child);

    return errors;
  };

  const handleNext = () => {
    const errors = validateCoverageForm(formData, septateCombinedRedux);

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      window.scrollTo(0, 0);
      const errorMessages = Object.values(errors).join('\n');
      alert(`Please fill all the required fields!\n${errorMessages}`);
      return;
    }

    setValidationErrors({});

    const removeEmptyMembers = (member) => {
      return (
        !member ||
        Object.values(member).every((value) => value === null || value === '')
      );
    };

    const removeEmptyChild = (childArray) => {
      return childArray.filter(
        (child) =>
          child.firstName?.trim() && child.lastName?.trim() && child.dob
      );
    };

    const insuredMembers = {
      self: removeEmptyMembers(formData.self) ? null : formData.self,
      spouse: removeEmptyMembers(formData.spouse) ? null : formData.spouse,
      parent: removeEmptyMembers(formData.parent) ? null : formData.parent,
      inLaw: removeEmptyMembers(formData.inLaw) ? null : formData.inLaw,
      child:
        removeEmptyChild(formData.child).length > 0 ? formData.child : null,
    };

    for (let key in insuredMembers) {
      if (insuredMembers[key] === null) {
        delete insuredMembers[key];
      }
    }

    const updatedData = { enquiryId, insuredMembers };
    dispatch(updateFirstPageData(updatedData))
      .then(() => {
        navigate(`/health-plan?enquiryId=${enquiryId}`);
      })
      .catch((error) => {
        console.error('Error updating first page data:', error);
      });
  };

  // Function to display the correct icon based on gender
  const getGenderIcon = (gender, type) => {
    if (gender === 'male') {
      if (type === 'self') return selfMale;
      if (type === 'spouse') return spouseMale;
      if (type === 'parent') return parentMale;
      if (type === 'inLaw') return inLawsMale;
      return maleChild;
    } else if (gender === 'female') {
      if (type === 'self') return selfFemale;
      if (type === 'spouse') return spouseFemale;
      if (type === 'parent') return parentFemale;
      if (type === 'inLaw') return inLawsFemale;
      return femaleChild;
    }
    return 'https://placehold.co/24x24?text=SI';
  };

  // Handle changes to form data
  const handleChange = (e, memberType, field, index = null) => {
    const updatedFormData = { ...formData };
    const newValue = e.target.value.trim();

    const valueToSave = newValue === '' ? null : newValue;

  // If we're updating a child
  if (index !== null && memberType === 'child') {
    updatedFormData.child = updatedFormData.child.map((child, i) => {
      if (i === index) {
        const updatedChild = { ...child, [field]: valueToSave };

        // Automatically set relation if gender is changed
        if (field === 'gender') {
          if (valueToSave === 'male') {
            updatedChild.relation = 'Son';
          } else if (valueToSave === 'female') {
            updatedChild.relation = 'Daughter';
          } else {
            updatedChild.relation = null; // fallback if gender is unknown
          }
        }

        return updatedChild;
      }
      return child;
    });
  } else {
    // Not a child (self, spouse, etc.)
    updatedFormData[memberType] = {
      ...updatedFormData[memberType],
      [field]: valueToSave,
    };
  }

  // Remove member if all fields are empty (excluding children)
  if (memberType !== 'child' && isMemberEmpty(updatedFormData[memberType])) {
    delete updatedFormData[memberType];
  }

  setFormData(updatedFormData);
};


  // Helper function to check if all fields in a member object are null or empty
  const isMemberEmpty = (member) => {
    return (
      !member ||
      Object.values(member).every((value) => value === null || value === '')
    );
  };

  return (
    <FormContainer>
      <Title>📝 Let’s Get Your Family Covered</Title>

      <NoticeBox>
        <div>
          <img src={aadharLogo} alt="Aadhar" width="50" /> Please add details as
          per Aadhar
        </div>
      </NoticeBox>

      {/* Self Details */}
      {septateCombinedRedux?.selectedMembers?.self === true && (
        <MemberCard>
          <SectionTitle>
            <Icon>
              <img
                src={getGenderIcon(formData.self?.gender, 'self')}
                alt="Self Icon"
                style={{ width: '24px', height: '24px' }}
              />
            </Icon>
            Myself
          </SectionTitle>
          <FormRow>
            <InputWrapper>
              <Label>First Name<Span>*</Span></Label>
              <Input
                type="text"
                value={formData.self?.firstName || ''}
                onChange={(e) => handleChange(e, 'self', 'firstName')}
              />
            </InputWrapper>
            <InputWrapper>
              <Label>Last Name<Span>*</Span></Label>
              <Input
                type="text"
                value={formData.self?.lastName || ''}
                onChange={(e) => handleChange(e, 'self', 'lastName')}
              />
            </InputWrapper>
            <InputWrapper>
              <Label>DOB<Span>*</Span></Label>
              <Input
                type="date"
                value={formData.self?.dob || ''}
                onChange={(e) => handleChange(e, 'self', 'dob')}
              />
            </InputWrapper>
          </FormRow>
        </MemberCard>
      )}

      {/* Spouse Details */}
      {septateCombinedRedux?.selectedMembers?.spouse === true && (
        <MemberCard>
          <SectionTitle>
            <Icon>
              <img
                src={getGenderIcon(formData.spouse?.gender, 'spouse')}
                alt="Spouse Icon"
                style={{ width: '24px', height: '24px' }}
              />
            </Icon>
            Spouse
          </SectionTitle>
          <FormRow>
            <InputWrapper>
              <Label>First Name<Span>*</Span></Label>
              <Input
                type="text"
                value={formData.spouse?.firstName || ''}
                onChange={(e) => handleChange(e, 'spouse', 'firstName')}
              />
            </InputWrapper>
            <InputWrapper>
              <Label>Last Name<Span>*</Span></Label>
              <Input
                type="text"
                value={formData.spouse?.lastName || ''}
                onChange={(e) => handleChange(e, 'spouse', 'lastName')}
              />
            </InputWrapper>
            <InputWrapper>
              <Label>DOB<Span>*</Span></Label>
              <Input
                type="date"
                value={formData.spouse?.dob || ''}
                onChange={(e) => handleChange(e, 'spouse', 'dob')}
              />
            </InputWrapper>
          </FormRow>
        </MemberCard>
      )}

      {/* Parent Details */}
      {septateCombinedRedux?.selectedMembers?.parent === true && (
        <MemberCard>
          <SectionTitle>
            <Icon>
              <img
                src={getGenderIcon(formData.parent?.gender, 'parent')}
                alt="Parent Icon"
                style={{ width: '24px', height: '24px' }}
              />
            </Icon>
            Parent
          </SectionTitle>
          <FormRow>
            <InputWrapper>
              <Label>First Name<Span>*</Span></Label>
              <Input
                type="text"
                value={formData.parent?.firstName || ''}
                onChange={(e) => handleChange(e, 'parent', 'firstName')}
              />
            </InputWrapper>
            <InputWrapper>
              <Label>Last Name<Span>*</Span></Label>
              <Input
                type="text"
                value={formData.parent?.lastName || ''}
                onChange={(e) => handleChange(e, 'parent', 'lastName')}
              />
            </InputWrapper>
            <InputWrapper>
              <Label>DOB<Span>*</Span></Label>
              <Input
                type="date"
                value={formData.parent?.dob || ''}
                onChange={(e) => handleChange(e, 'parent', 'dob')}
              />
            </InputWrapper>
          </FormRow>
        </MemberCard>
      )}

      {/* In-Law Details */}
      {septateCombinedRedux?.selectedMembers?.inLaw === true && (
        <MemberCard>
          <SectionTitle>
            <Icon>
              <img
                src={getGenderIcon(formData.inLaw?.gender, 'inLaw')}
                alt="In-Law Icon"
                style={{ width: '24px', height: '24px' }}
              />
            </Icon>
            In-Law
          </SectionTitle>
          <FormRow>
            <InputWrapper>
              <Label>First Name<Span>*</Span></Label>
              <Input
                type="text"
                value={formData.inLaw?.firstName || ''}
                onChange={(e) => handleChange(e, 'inLaw', 'firstName')}
              />
            </InputWrapper>
            <InputWrapper>
              <Label>Last Name<Span>*</Span></Label>
              <Input
                type="text"
                value={formData.inLaw?.lastName || ''}
                onChange={(e) => handleChange(e, 'inLaw', 'lastName')}
              />
            </InputWrapper>
            <InputWrapper>
              <Label>DOB<Span>*</Span></Label>
              <Input
                type="date"
                value={formData.inLaw?.dob || ''}
                onChange={(e) => handleChange(e, 'inLaw', 'dob')}
              />
            </InputWrapper>
          </FormRow>
        </MemberCard>
      )}

      {/* Children Details */}
      {formData?.child?.map((child, i) => (
        <MemberCard key={`child-${i}`}>
          <SectionTitle>
            <Icon>
              <img
                src={getGenderIcon(child?.gender, 'child')}
                alt="Child Icon"
                style={{ width: '24px', height: '24px' }}
              />
            </Icon>
            Child {i + 1}
          </SectionTitle>
          <FormRow>
            <InputWrapper>
              <Label>First Name<Span>*</Span></Label>
              <Input
                type="text"
                value={child?.firstName || ''}
                onChange={(e) => handleChange(e, 'child', 'firstName', i)}
              />
            </InputWrapper>
            <InputWrapper>
              <Label>Last Name<Span>*</Span></Label>
              <Input
                type="text"
                value={child?.lastName || ''}
                onChange={(e) => handleChange(e, 'child', 'lastName', i)}
              />
            </InputWrapper>
            <InputWrapper>
              <Label>DOB<Span>*</Span></Label>
              <Input
                type="date"
                value={child?.dob || ''}
                onChange={(e) => handleChange(e, 'child', 'dob', i)}
              />
            </InputWrapper>
          </FormRow>
        </MemberCard>
      ))}

      <NextButton onClick={handleNext}>Next</NextButton>
    </FormContainer>
  );
};

export default CoverageForm;
